import React, { useEffect, useRef, useState } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import { elementWasClickedClass, elementWasClickedId } from '@upsales/components/Utils/elementWasClicked';
import mapboxgl, { LngLatLike, LngLatBoundsLike } from 'mapbox-gl';
import { SearchBox } from '@mapbox/search-js-react';

import { renderToString } from 'react-dom/server';

import { Flex, Icon, Text, Label, Loader, ButtonSelect } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';

import GeocodeResource from 'App/resources/Geocode';

import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.scss';

import { PrimaryButton } from '@upsales/components/Buttons';
import useDefaultCenter from './useDefaultCenter';
import useAreaSelect from './useAreaSelect';
import { Address } from 'App/resources/Model/Geocode';
import type { Pin } from './mapPins/MapPinTypes';
import config from 'App/babel/config';

import type { Props, PinOnLocation, Bounds } from './MapTypes';

type PaintedPins = Record<string, mapboxgl.Marker>;

const getPinKey = (pin: { latitude: number; longitude: number }) => `${pin.latitude}-${pin.longitude}`;

const Popup = ({
	map,
	activePin,
	defaultOnClick
}: {
	map: mapboxgl.Map;
	activePin: Pin<any> | null;
	defaultOnClick: (pin: Pin<any>) => void;
}) => {
	const popupRef = useRef<mapboxgl.Popup | null>(null);
	const shownKey = useRef<string | null>(null);
	const { t } = useTranslation();

	useEffect(() => {
		if (!map) return;

		popupRef.current = new mapboxgl.Popup({
			closeOnClick: false,
			focusAfterOpen: false,
			closeButton: false,
			maxWidth: '300px',
			offset: 20
		});

		return () => {
			popupRef.current?.remove();
		};
	}, [map]);

	useEffect(() => {
		if (!activePin) {
			popupRef.current?.remove();
			shownKey.current = null;
			return;
		}

		if (!popupRef.current) {
			return;
		}

		// Re-rendering the same pin (its data changed) replaces the HTML, which would scroll a long
		// list back to the top; the old scroll offsets are put back by element position.
		const refreshed = shownKey.current === getPinKey(activePin);
		const scrolled = refreshed
			? Array.from(popupRef.current.getElement()?.querySelectorAll<HTMLElement>('*') ?? [], el => el.scrollTop)
			: [];
		shownKey.current = getPinKey(activePin);

		if (activePin.renderDetails) {
			const details = activePin.renderDetails(activePin, t);
			if (details) {
				popupRef.current.setHTML(renderToString(details as React.ReactElement));
			}
		}

		popupRef.current.setLngLat([activePin.longitude, activePin.latitude]);
		popupRef.current.addTo(map);

		const element = popupRef.current.getElement();
		element?.querySelectorAll<HTMLElement>('*').forEach((el, index) => {
			if (scrolled[index]) {
				el.scrollTop = scrolled[index];
			}
		});

		element?.classList.add('mapboxPopupContent');
		const onClick = (event: any) => {
			event.stopPropagation();

			const pinClickListeners = activePin.events?.click || [];
			const pinClickTriggered = pinClickListeners.find(pinListener => {
				if (pinListener.alwaysTrigger) {
					return true;
				} else if (pinListener.className) {
					return elementWasClickedClass(pinListener.className, event);
				} else if (pinListener.id) {
					return elementWasClickedId(pinListener.id, event);
				}
				return false;
			});

			if (pinClickTriggered) {
				pinClickTriggered.handler(event, activePin);
				return;
			}

			if (elementWasClickedClass('clickable', event)) {
				defaultOnClick(activePin);
			}
		};
		element?.addEventListener('click', onClick);
		return () => element?.removeEventListener('click', onClick);
	}, [activePin]);

	return null;
};

const MapboxMap = <T,>({
	centerMapOnPins = false,
	keepPinsOnUpdate = false,
	allowScrollZoom = true,
	defaultCenter,
	showTools = true,
	pins = [],
	onBoundsChange,
	onPinSelect,
	renderQuickFilters,
	hideSearch = false,
	allowFullScreen = false,
	areaSelect
}: Props<T>) => {
	const classes = new BemClass('Map');
	const { t } = useTranslation();

	const mapInstance = useRef<mapboxgl.Map | null>(null);
	const mapElem = useRef<HTMLDivElement>(null);
	const paintedPins = useRef<PaintedPins>({});
	// A marker outlives the pin it was painted for while its key holds, so its click handler
	// reads the current pin from here instead of closing over the first one.
	const currentPins = useRef<Record<string, Pin<T>>>({});
	const paintedLooks = useRef<Record<string, string>>({});

	const mapCenter = useDefaultCenter(defaultCenter, centerMapOnPins);
	const mapStyles = [
		{ title: t('default.map'), value: 'mapbox://styles/mapbox/streets-v12' },
		{ title: t('default.satellite'), value: 'mapbox://styles/mapbox/satellite-streets-v12' }
	];

	const [inited, setInited] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedStyle, setSelectedStyle] = useState(mapStyles[0].value);
	const [activePin, setActivePin] = useState<Pin<T> | null>(null);
	useAreaSelect(mapInstance, inited, areaSelect, pins, classes.elem('areaBox').b());

	const initMap = () => {
		const mapWrapper = mapElem.current;
		if (mapWrapper && mapCenter && !mapInstance.current) {
			const pos = { lat: mapCenter!.latitude, lng: mapCenter!.longitude };
			const mapOpts = {
				container: mapWrapper,
				style: selectedStyle,
				zoom: 14,
				center: [pos.lng, pos.lat] as LngLatLike,
				scrollZoom: allowScrollZoom
			};

			mapboxgl.accessToken = config.MAPBOX_TOKEN;
			mapInstance.current = new mapboxgl.Map(mapOpts);

			if (showTools) {
				mapInstance.current.addControl(
					new mapboxgl.NavigationControl({ showZoom: allowScrollZoom }),
					'bottom-right'
				);
			}

			if (allowFullScreen) {
				mapInstance.current.addControl(new mapboxgl.FullscreenControl(), 'top-right');
			}

			mapInstance.current.on('load', () => {
				setInited(true);
			});
		}
	};

	const buildMarker = (pin: Pin<T>) => {
		const marker = (
			<>
				<Flex
					justifyContent="center"
					alignItems="center"
					className={classes.elem('markerIconWrapper').mod({ clickable: pin.clickable }).b()}
				>
					<div className={classes.elem('markerPin').b()} />
					{pin.icon ? (
						<Icon className={classes.elem('markerIcon').b()} name={pin.icon as any} color={pin.iconColor} />
					) : null}
					{pin.iconText ? (
						<Text className={classes.elem('markerIconText').b()} bold color={pin.iconColor ?? 'inherit'}>
							{pin.iconText}
						</Text>
					) : null}
					{pin.selected ? (
						<Flex
							alignItems="center"
							justifyContent="center"
							className={classes.elem('markerSelected').b()}
						>
							<Icon name="check" color="white" />
						</Flex>
					) : null}
				</Flex>
			</>
		);
		const myMarker = renderToString(marker);

		const content = document.createElement('div');
		content.classList.add(classes.elem('marker').b());
		content.style.setProperty('--backgroundColor', pin.backgroundColor);
		if (pin.color) {
			content.style.setProperty('--color', pin.color);
		}
		content.style.setProperty('--borderColor', pin.border);

		content.innerHTML = myMarker;

		return content;
	};

	const getPinLook = (pin: Pin<T>) =>
		[pin.backgroundColor, pin.border, pin.icon, pin.iconText, pin.selected].join('|');
	const removePins = (pins: Pin<T>[]) => {
		const pinsToKeep = pins.map(p => getPinKey(p));
		for (const [pinKey, pin] of Object.entries(paintedPins.current)) {
			if (!pinsToKeep.includes(pinKey)) {
				pin.remove();
				delete paintedPins.current[pinKey];
				delete paintedLooks.current[pinKey];
				delete currentPins.current[pinKey];
			}
		}
	};

	const handlePinClick = (pin: Pin<T>) => {
		setActivePin(activePin => {
			if (!activePin) {
				return pin;
			}

			if (getPinKey(pin) === getPinKey(activePin)) {
				return null;
			}

			return pin;
		});
	};

	const setupPins = () => {
		if (!mapInstance.current) {
			return;
		}

		const seen = new Set<string>();
		for (const pin of pins) {
			const pinKey = getPinKey(pin);
			if (seen.has(pinKey)) {
				continue;
			}
			seen.add(pinKey);
			const look = getPinLook(pin);
			currentPins.current[pinKey] = pin;
			if (paintedPins.current[pinKey]) {
				if (paintedLooks.current[pinKey] === look) {
					continue;
				}
				paintedPins.current[pinKey].remove();
			}

			const marker = new mapboxgl.Marker({ element: buildMarker(pin) })
				.setLngLat([pin.longitude, pin.latitude])
				.addTo(mapInstance.current);
			paintedPins.current[pinKey] = marker;
			paintedLooks.current[pinKey] = look;

			const element = marker.getElement();
			element.addEventListener('click', (event: any) => {
				event.stopPropagation();
				const current = currentPins.current[pinKey];

				const pinClickListeners = current.events?.click || [];
				const pinClickTriggered = pinClickListeners.find(pinListener => {
					if (pinListener.alwaysTrigger) {
						return true;
					} else if (pinListener.className) {
						return elementWasClickedClass(pinListener.className, event);
					} else if (pinListener.id) {
						return elementWasClickedId(pinListener.id, event);
					}
					return false;
				});

				if (pinClickTriggered) {
					pinClickTriggered.handler(event, current);
					return;
				}

				handlePinClick(current);
			});
		}

		if (centerMapOnPins && pins.length) {
			let { north, south, west, east } = pins.reduce(
				(acc, pin) => {
					if (!acc.south || pin.latitude < acc.south) {
						acc.south = pin.latitude;
					}
					if (!acc.north || pin.latitude > acc.north) {
						acc.north = pin.latitude;
					}
					if (!acc.west || pin.longitude < acc.west) {
						acc.west = pin.longitude;
					}
					if (!acc.east || pin.longitude > acc.east) {
						acc.east = pin.longitude;
					}
					return acc;
				},
				{ north: undefined, south: undefined, west: undefined, east: undefined } as any
			);

			// Ensure bounds have a minimum size to prevent extreme zoom levels
			// This handles single pins AND multiple pins at the same coordinates
			if (north - south < 0.01) {
				north += 0.01;
				south -= 0.01;
			}
			if (east - west < 0.01) {
				east += 0.01;
				west -= 0.01;
			}

			const pinBounds: LngLatBoundsLike = [west, south, east, north];
			mapInstance.current.fitBounds(pinBounds, { padding: 100, linear: true, duration: 0, maxZoom: 15 });
		}
	};

	const getPinnedContent = (location: PinOnLocation) => {
		const { latitude, longitude, address, zipcode, city } = location;

		const latitudeLongitude = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
		const content = (
			<Flex className={classes.elem('pinnedContent').b()} justifyContent="space-between" alignItems="center">
				<Flex direction="column" space="mrm">
					<Label>{address?.trim?.()}</Label>
					<Text color="grey-11" size="sm" space="mbm">
						{zipcode} {city}
					</Text>
					<div className={classes.elem('divider').b()} />
					<Flex>
						<Text color="medium-blue" size="sm" space="mtm">
							{latitudeLongitude}
						</Text>
					</Flex>
				</Flex>
				<PrimaryButton id={classes.elem('pinSelect').b()}>{t('default.select')}</PrimaryButton>
			</Flex>
		);
		return renderToString(content);
	};

	const setupPinnedContent = (location: PinOnLocation, latLngEvent: any) => {
		if (!mapInstance.current) {
			return;
		}

		const popup = new mapboxgl.Popup({ offset: 10, maxWidth: 'none' })
			.setLngLat(latLngEvent)
			.setHTML(getPinnedContent(location));

		popup.on('open', () => {
			const pinSelect = document.getElementById('Map__pinSelect');
			pinSelect?.addEventListener(
				'click',
				() => {
					onPinSelect?.(location);
				},
				{ once: true }
			);
		});

		popup.addTo(mapInstance.current);
	};

	const onMapClick = async (latLng: mapboxgl.LngLat, address?: Address) => {
		if (!mapInstance.current) {
			return;
		}

		let pinOnLocation: PinOnLocation = {
			latitude: latLng.lat,
			longitude: latLng.lng
		};

		if (address) {
			pinOnLocation = { ...pinOnLocation, ...address };
		} else {
			const { data: addressFound } = await GeocodeResource.lookupLatLong(
				pinOnLocation.latitude,
				pinOnLocation.longitude
			);
			if (addressFound) {
				pinOnLocation = {
					...pinOnLocation,
					...addressFound
				};
			}
		}

		setupPinnedContent(pinOnLocation, latLng);
	};

	const setupAddPinMode = () => {
		if (!mapInstance.current) {
			return;
		}

		mapInstance.current.on('click', (event: mapboxgl.MapMouseEvent) => {
			onMapClick(event.lngLat);
		});
	};

	const getFormattedBounds = (): Bounds | null => {
		if (!mapInstance.current) {
			return null;
		}
		const bounds = mapInstance.current.getBounds();
		if (!bounds) {
			return null;
		}
		return {
			latitude: {
				low: bounds.getSouthWest().lat,
				high: bounds.getNorthEast().lat
			},
			longitude: {
				low: bounds.getSouthWest().lng,
				high: bounds.getNorthEast().lng
			}
		};
	};

	const setupBoundsChangedListener = () => {
		if (!onBoundsChange || !mapInstance.current) {
			return;
		}

		// Fire initial bounds so consumers can load data immediately
		const initialBounds = getFormattedBounds();
		if (initialBounds) {
			onBoundsChange(initialBounds);
		}

		mapInstance.current.on('moveend', () => {
			const formattedBounds = getFormattedBounds();
			if (formattedBounds) {
				onBoundsChange(formattedBounds);
			}
		});
	};

	useEffect(() => {
		initMap();
	}, [mapCenter]);

	useEffect(() => {
		if (!inited) {
			return;
		}

		setupPins();

		if (onBoundsChange) {
			setupBoundsChangedListener();
		}

		if (onPinSelect) {
			setupAddPinMode();
		}

		mapInstance.current?.on('click', () => {
			setActivePin(null);
		});
	}, [inited]);

	useEffect(() => {
		if (!keepPinsOnUpdate) {
			removePins(pins);
			setActivePin(active => (active ? pins.find(pin => getPinKey(pin) === getPinKey(active)) ?? null : null));
		}

		setupPins();
	}, [pins]);

	return (
		<div className={classes.b()}>
			{!inited ? (
				<div className={classes.b()}>
					<Flex alignItems="center" justifyContent="center" className={classes.elem('loader').b()}>
						<Loader />
					</Flex>
				</div>
			) : null}

			{!hideSearch && mapInstance.current && inited ? (
				<div className={classes.elem('searchBox').mod('mapbox').b()}>
					<SearchBox
						accessToken={config.MAPBOX_TOKEN}
						map={mapInstance.current}
						value={searchQuery}
						onChange={setSearchQuery}
						placeholder={t('default.searchAddress')}
						options={{
							proximity: {
								lng: mapInstance.current.getCenter().lng,
								lat: mapInstance.current.getCenter().lat
							}
						}}
						mapboxgl={mapboxgl}
						marker
					/>
				</div>
			) : null}

			{showTools && inited ? (
				<div className={classes.elem('styleSelector').mod({ searchActive: !hideSearch }).b()}>
					<ButtonSelect
						options={mapStyles}
						value={selectedStyle}
						onChange={value => {
							setSelectedStyle(value);
							mapInstance.current?.setStyle(value);
						}}
					/>
				</div>
			) : null}

			<div className={classes.elem('map').b()} ref={mapElem} />

			{mapInstance.current ? (
				<Popup map={mapInstance.current} activePin={activePin} defaultOnClick={handlePinClick} />
			) : null}

			{renderQuickFilters && inited ? (
				<Flex className={classes.elem('quickFilters').b()} justifyContent="center">
					{renderQuickFilters()}
				</Flex>
			) : null}
		</div>
	);
};

export default MapboxMap;
